import {
  effect,
  EffectRef,
  inject,
  Injectable,
  Injector,
  signal,
  untracked,
} from "@angular/core";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { TrailNode, TrailRouteDef } from "./navigation-trail.model";
import {
  buildNode,
  extractParams,
  matchTrailRoute,
} from "./navigation-trail.match";

/** Nombre de maillons conservés en mémoire (l'affichage en montre moins). */
export const NAV_TRAIL_MAX_NODES = 10;

const STORAGE_KEY = "justup.navTrail.v1";

interface TrailSnapshot {
  /** Id de l'utilisateur au moment de l'écriture — anti-fuite entre comptes. */
  uid: string | null;
  nodes: TrailNode[];
}

/**
 * Fil de navigation : la pile des pages traversées, alimentée par le Router.
 *
 * Ce n'est PAS un fil d'Ariane hiérarchique (il ne décrit pas l'arborescence
 * des routes) mais un **historique de parcours** : « liste des articles →
 * article ART-42 → commande client CC-118 ».
 *
 * Règles de pile (cf. `plan/navigation-trail.md`) :
 * - une route `silent` ne produit rien ;
 * - naviguer **empile TOUJOURS**, même vers une page déjà traversée : le fil
 *   raconte le parcours réel, donc un même élément peut y figurer plusieurs
 *   fois. On ne remonte JAMAIS l'historique tout seul ;
 * - **sauf** si la clé est celle du **dernier** maillon : c'est alors le même
 *   écran (changement d'onglet, redirection automatique) → on le met à jour ;
 * - **sauf** si la navigation vient d'un **clic dans le fil**
 *   (`state.trailJumpTo`) : là seulement on tronque jusqu'au maillon visé ;
 * - plafonné à `NAV_TRAIL_MAX_NODES`.
 *
 * AUCUNE route ne remet le fil à zéro : l'effacement est un geste explicite
 * de l'utilisateur (`clear()`, menu « … » du fil) ou la conséquence d'un hard
 * logout. Une entité supprimée est retirée du fil par `forgetEntity()`.
 *
 * Le service ne charge JAMAIS de donnée : il lit les stores déjà alimentés par
 * les pages, et met à jour le libellé quand la ref arrive.
 */
@Injectable({ providedIn: "root" })
export class NavigationTrailService {
  private router = inject(Router);
  private injector = inject(Injector);

  private readonly _nodes = signal<TrailNode[]>([]);
  readonly nodes = this._nodes.asReadonly();

  /** Un effet de résolution de libellé par maillon, détruit avec lui. */
  private readonly resolvers = new Map<string, EffectRef>();

  /** Compteur d'instances — voir `TrailNode.uid`. */
  private uidSeq = 1;

  /** Posé par `router.navigate(…, { state: { trailSilent: true } })`. */
  private pendingSilent = false;

  /**
   * Clé visée par un clic dans le fil (`state.trailJumpTo`). C'est le SEUL
   * cas où l'on remonte l'historique : toute autre navigation empile.
   */
  private pendingJumpTo: string | null = null;

  constructor() {
    this.restore();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const state = this.router.getCurrentNavigation()?.extras?.state;
        this.pendingSilent = state?.["trailSilent"] === true;
        this.pendingJumpTo = state?.["trailJumpTo"] ?? null;
        return;
      }
      if (event instanceof NavigationEnd) {
        const silent = this.pendingSilent;
        const jumpTo = this.pendingJumpTo;
        this.pendingSilent = false;
        this.pendingJumpTo = null;
        if (!silent) {
          this.track(event.urlAfterRedirects, jumpTo);
        }
      }
    });
  }

  /** Vide le fil (hard login, changement de contexte…). */
  clear(): void {
    this.resolvers.forEach((ref) => ref.destroy());
    this.resolvers.clear();
    this._nodes.set([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // localStorage indisponible : l'état en mémoire suffit.
    }
  }

  /**
   * Retire du fil tout maillon qui désigne l'entité `id` — appelé quand une
   * entité est SUPPRIMÉE (`EntityFacadeService.remove`), pour qu'on ne
   * propose plus de revenir sur une fiche qui n'existe plus.
   *
   * Le rapprochement se fait sur le suffixe de la clé (`article:<uuid>`,
   * `cc:<uuid>`…) : un uuid étant unique, un même id sous plusieurs préfixes
   * (`cc:X` et `prep-cc:X`) tombe correctement d'un seul geste, sans avoir à
   * dupliquer l'id dans chaque déclaration du registre.
   */
  forgetEntity(id: string): void {
    if (!id) return;
    const suffix = `:${id}`;
    const current = this._nodes();
    const next = current.filter((n) => !n.key.endsWith(suffix));
    if (next.length === current.length) return;

    for (const node of current) {
      if (node.key.endsWith(suffix)) {
        this.resolvers.get(node.key)?.destroy();
        this.resolvers.delete(node.key);
      }
    }
    this._nodes.set(next);
    this.persist();
  }

  /**
   * Enregistre une URL dans le fil.
   *
   * @param jumpTo clé visée par un clic dans le fil — seul cas de troncature.
   */
  track(url: string, jumpTo: string | null = null): void {
    const def = matchTrailRoute(url);
    if (def?.silent) return;

    const node = buildNode(url, def);
    if (!node) return;

    const current = this._nodes();
    // Un même élément peut déjà avoir été résolu ailleurs dans la pile : on
    // reprend son libellé pour ne pas réafficher le repli i18n le temps que
    // le store réponde.
    const known = current.find((n) => n.key === node.key && n.resolved);
    const fresh: TrailNode = known
      ? { ...node, label: known.label, sub: known.sub, resolved: true }
      : node;

    let next: TrailNode[];
    const jumpIndex = jumpTo ? current.findIndex((n) => n.key === jumpTo) : -1;
    const last = current.length ? current[current.length - 1] : null;

    if (jumpIndex >= 0) {
      // Clic dans le fil : on remonte jusqu'au maillon visé. Il garde son uid,
      // sinon l'affichage le verrait sortir puis rentrer.
      next = [
        ...current.slice(0, jumpIndex),
        { ...fresh, uid: current[jumpIndex].uid },
      ];
    } else if (last?.key === node.key) {
      // Même écran (onglet, redirection auto) : mise à jour sur place — uid
      // conservé, donc aucune animation parasite au changement d'onglet.
      next = [...current.slice(0, -1), { ...fresh, uid: last.uid }];
    } else {
      // Tout le reste EMPILE, y compris un retour sur une page déjà visitée.
      next = [...current, { ...fresh, uid: this.uidSeq++ }];
    }

    if (next.length > NAV_TRAIL_MAX_NODES) {
      next = next.slice(next.length - NAV_TRAIL_MAX_NODES);
    }

    const kept = new Set(next.map((n) => n.key));
    this.resolvers.forEach((ref, key) => {
      if (!kept.has(key)) {
        ref.destroy();
        this.resolvers.delete(key);
      }
    });

    this._nodes.set(next);
    this.persist();
    this.startResolver(node, def);
  }

  /**
   * Branche la résolution du libellé réel (ref de l'élément) sur le store de
   * l'entité. `getById` est appelé HORS contexte réactif (il écrit dans la map
   * du store quand l'entité est inconnue), l'effet ne fait que lire le signal
   * retourné.
   */
  private startResolver(node: TrailNode, def: TrailRouteDef | null): void {
    if (!def?.resolve || this.resolvers.has(node.key)) return;

    const params = extractParams(node.url, def.match);
    def
      .resolve(params, this.injector)
      .then((source) => {
        // Le maillon a pu sortir de la pile pendant l'import dynamique.
        if (!this._nodes().some((n) => n.key === node.key)) return;

        const ref = effect(
          () => {
            const value = source();
            const label = (
              typeof value === "string" ? value : value?.label
            )?.trim();
            if (!label) return;
            const sub =
              typeof value === "string" ? undefined : value?.sub?.trim();
            // `untracked` : sans ça l'effet dépendrait de `_nodes` (lu par
            // setLabel/persist) et se ré-déclencherait sur sa propre écriture.
            untracked(() => this.setLabel(node.key, label, sub));
          },
          { injector: this.injector, allowSignalWrites: true },
        );
        this.resolvers.set(node.key, ref);
      })
      .catch(() => {
        // Feature indisponible (droits, lazy chunk) : le libellé de repli reste.
      });
  }

  /**
   * Remplace le libellé des maillons de cette clé (résolution de ref,
   * renommage…). TOUTES les occurrences sont mises à jour : le même élément
   * peut figurer plusieurs fois dans le parcours.
   */
  setLabel(key: string, label: string, sub?: string): void {
    const current = this._nodes();
    if (
      !current.some(
        (n) => n.key === key && (n.label !== label || n.sub !== sub),
      )
    ) {
      return;
    }

    this._nodes.set(
      current.map((n) =>
        n.key === key ? { ...n, label, sub, resolved: true } : n,
      ),
    );
    this.persist();
  }

  // ── Persistance ─────────────────────────────────────────────────────

  private persist(): void {
    try {
      const snapshot: TrailSnapshot = {
        uid: readCurrentUserId(),
        nodes: this._nodes(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // Quota / mode privé : le fil vit en mémoire pour cette session.
    }
  }

  private restore(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const snapshot = JSON.parse(raw) as TrailSnapshot;
      if (!Array.isArray(snapshot?.nodes)) return;
      // Fil d'un autre compte (login sans logout préalable) : on repart à zéro.
      if (snapshot.uid !== readCurrentUserId()) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      const restored = snapshot.nodes
        .filter(
          (n) => n && typeof n.key === "string" && typeof n.url === "string",
        )
        // Un snapshot d'une version antérieure n'a pas d'uid : on en attribue.
        .map((n) => ({ ...n, resolved: !!n.resolved, uid: n.uid || this.uidSeq++ }));
      this.uidSeq = Math.max(this.uidSeq, ...restored.map((n) => n.uid + 1));
      this._nodes.set(restored);
    } catch {
      // Snapshot corrompu : on l'ignore.
    }
  }
}

/**
 * Id de l'utilisateur courant, lu directement dans le stockage (`AuthService`
 * n'est PAS injecté ici : il dépend du Router et du socket, on éviterait de
 * justesse un cycle et on alourdirait le boot pour rien).
 */
function readCurrentUserId(): string | null {
  try {
    const raw = localStorage.getItem("USER");
    if (!raw) return null;
    // `LocalStorageService.putWithTTL` enveloppe la valeur : { value, ttl }.
    const wrapper = JSON.parse(raw);
    const user = JSON.parse(wrapper?.value ?? "null");
    return user?.id ?? null;
  } catch {
    return null;
  }
}
